import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { formatCLP, formatFecha, hoyISO } from '../lib/format';
import { calcularFinanzas, ahorradoPorMeta, reservadoMetaPorBolsillo } from '../lib/finanzas';
import { q, recalcularMeta } from '../lib/vinculos';
import { useAccion } from '../lib/useAccion';
import ErrorBanner from './ErrorBanner';

const ICONOS = ['🎯', '🏠', '✈️', '🚗', '📚', '💻', '💍', '🎓', '🏋️', '🌍', '🐾', '🎸', '💊', '👶', '🏖️', '🔑'];

function ProgressBar({ value, max }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div style={{ background: 'var(--border)', borderRadius: 99, height: 10, overflow: 'hidden', margin: '10px 0' }}>
      <div style={{
        width: `${pct}%`,
        height: '100%',
        background: pct >= 100 ? 'var(--success, #22c55e)' : 'var(--primary)',
        borderRadius: 99,
        transition: 'width 0.4s ease',
      }} />
    </div>
  );
}

const formVacio = { nombre: '', descripcion: '', icono: '🎯', monto_objetivo: '', fecha_objetivo: '' };

function Metas({ userId }) {
  const [data, setData] = useState({ metas: [], abonosMeta: [], bolsillos: [], movimientos: [], compromisos: [], abonos: [] });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(formVacio);
  const [movModal, setMovModal] = useState(null); // { tipo: 'abono' | 'retiro', meta }
  const [movForm, setMovForm] = useState({ bolsillo_id: '', monto: '' });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const { saving, error, setError, run } = useAccion();

  const loadData = useCallback(async () => {
    if (!userId) return;
    const [mt, am, b, m, c, a] = await Promise.all([
      q(supabase.from('metas').select('*').eq('user_id', userId).order('created_at', { ascending: true })),
      q(supabase.from('abonos_meta').select('*').eq('user_id', userId)),
      q(supabase.from('bolsillos').select('*').eq('user_id', userId).order('created_at')),
      q(supabase.from('bolsillo_movimientos').select('*').eq('user_id', userId)),
      q(supabase.from('compromisos').select('*').eq('user_id', userId)),
      q(supabase.from('abonos').select('*').eq('user_id', userId)),
    ]);
    setData({ metas: mt || [], abonosMeta: am || [], bolsillos: b || [], movimientos: m || [], compromisos: c || [], abonos: a || [] });
  }, [userId]);

  useEffect(() => {
    loadData().catch(e => setError('Error cargando datos: ' + e.message)).finally(() => setLoading(false));
  }, [loadData, setError]);

  const { metas, abonosMeta, bolsillos } = data;
  const fin = calcularFinanzas(data);
  const ahorrado = ahorradoPorMeta(abonosMeta);
  const nombreBolsillo = (id) => (id ? bolsillos.find(b => b.id === id)?.nombre || 'Categoría eliminada' : 'Sin categoría');

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.nombre || !form.monto_objetivo) return;
    const ok = await run(() => q(supabase.from('metas').insert({
      user_id: userId,
      nombre: form.nombre,
      descripcion: form.descripcion,
      icono: form.icono,
      monto_objetivo: parseFloat(form.monto_objetivo),
      monto_actual: 0,
      fecha_objetivo: form.fecha_objetivo || null,
      completada: false,
    })), loadData);
    if (ok) {
      setForm(formVacio);
      setShowForm(false);
    }
  };

  // --- Abonar / retirar: el dinero sigue en la categoría, solo se reserva o se libera ---
  const abrirAbono = (meta) => {
    const mejor = [...bolsillos].sort((a, b) => fin.porBolsillo(b.id).disponible - fin.porBolsillo(a.id).disponible)[0];
    setMovForm({ bolsillo_id: mejor?.id || '', monto: '' });
    setMovModal({ tipo: 'abono', meta });
  };

  const abrirRetiro = (meta) => {
    const reservas = Object.entries(reservadoMetaPorBolsillo(abonosMeta, meta.id)).filter(([, v]) => v > 0);
    setMovForm({ bolsillo_id: reservas[0]?.[0] ?? '', monto: '' });
    setMovModal({ tipo: 'retiro', meta });
  };

  const handleMovMeta = async (e) => {
    e.preventDefault();
    const { tipo, meta } = movModal;
    const monto = parseFloat(movForm.monto);
    if (!monto || monto <= 0) return;
    if (tipo === 'abono') {
      if (!movForm.bolsillo_id) { setError('Elige de qué categoría sale el ahorro.'); return; }
      const disponible = fin.porBolsillo(movForm.bolsillo_id).disponible;
      if (monto > disponible) { setError(`No tienes suficiente disponible en esa categoría (${formatCLP(disponible)}).`); return; }
    } else {
      const reservado = reservadoMetaPorBolsillo(abonosMeta, meta.id)[movForm.bolsillo_id] || 0;
      if (monto > reservado) { setError(`Solo hay ${formatCLP(reservado)} reservados ahí.`); return; }
    }
    const ok = await run(async () => {
      await q(supabase.from('abonos_meta').insert({
        user_id: userId,
        meta_id: meta.id,
        bolsillo_id: movForm.bolsillo_id || null,
        monto: tipo === 'abono' ? monto : -monto,
        fecha: hoyISO(),
      }));
      await recalcularMeta(meta.id);
    }, loadData);
    if (ok) setMovModal(null);
  };

  const handleEdit = (meta) => {
    setEditingId(meta.id);
    setEditForm({
      nombre: meta.nombre,
      descripcion: meta.descripcion || '',
      icono: meta.icono || '🎯',
      monto_objetivo: meta.monto_objetivo,
      fecha_objetivo: meta.fecha_objetivo || '',
    });
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    const id = editingId;
    const ok = await run(async () => {
      await q(supabase.from('metas').update({
        nombre: editForm.nombre,
        descripcion: editForm.descripcion,
        icono: editForm.icono,
        monto_objetivo: parseFloat(editForm.monto_objetivo),
        fecha_objetivo: editForm.fecha_objetivo || null,
      }).eq('id', id));
      await recalcularMeta(id); // si cambió el objetivo, puede pasar a completada o no
    }, loadData);
    if (ok) setEditingId(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta meta?\n\nEl dinero reservado vuelve a quedar disponible en sus categorías.')) return;
    await run(() => q(supabase.from('metas').delete().eq('id', id)), loadData);
  };

  const metasActivas = metas.filter(m => !m.completada);
  const metasCompletadas = metas.filter(m => m.completada);
  const totalAhorrado = metas.reduce((s, m) => s + (ahorrado[m.id] || 0), 0);
  const totalObjetivo = metas.reduce((s, m) => s + (m.monto_objetivo || 0), 0);

  const DetalleReservas = ({ meta }) => {
    const reservas = Object.entries(reservadoMetaPorBolsillo(abonosMeta, meta.id)).filter(([, v]) => v > 0);
    if (!reservas.length) return null;
    return (
      <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: '4px 0 0' }}>
        Reservado en: {reservas.map(([id, v]) => `${nombreBolsillo(id)} ${formatCLP(v)}`).join(' · ')}
      </p>
    );
  };

  if (loading) return (
    <div className="bolsillos-loading"><div className="loading-spinner"></div><p>Cargando...</p></div>
  );

  return (
    <div className="metas-page">
      <div className="page-header">
        <div>
          <h2>Mis Metas</h2>
          <p className="page-subtitle">Ahorra con propósito, celebra cada avance</p>
        </div>
        <button className="btn-primary" onClick={() => setShowForm(s => !s)}>
          {showForm ? 'Cancelar' : '+ Nueva meta'}
        </button>
      </div>

      <ErrorBanner error={error} onClose={() => setError('')} />

      {/* Resumen */}
      {metas.length > 0 && (
        <div className="summary-cards" style={{ marginBottom: 24 }}>
          <div className="card">
            <div className="card-icon">🎯</div>
            <div className="card-content">
              <h4>Metas activas</h4>
              <p className="card-amount neutral">{metasActivas.length}</p>
            </div>
          </div>
          <div className="card">
            <div className="card-icon">💰</div>
            <div className="card-content">
              <h4>Total ahorrado</h4>
              <p className="card-amount positive">{formatCLP(totalAhorrado)}</p>
            </div>
          </div>
          <div className="card">
            <div className="card-icon">🏆</div>
            <div className="card-content">
              <h4>Completadas</h4>
              <p className="card-amount positive">{metasCompletadas.length}</p>
            </div>
          </div>
          <div className="card">
            <div className="card-icon">📍</div>
            <div className="card-content">
              <h4>Por alcanzar</h4>
              <p className="card-amount neutral">{formatCLP(Math.max(0, totalObjetivo - totalAhorrado))}</p>
            </div>
          </div>
        </div>
      )}

      {/* Formulario de nueva meta */}
      {showForm && (
        <div className="card" style={{ marginBottom: 24 }}>
          <h3 style={{ marginBottom: 16, fontSize: '1rem' }}>Nueva meta de ahorro</h3>
          <form onSubmit={handleCreate}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
              <div className="form-group">
                <label>Nombre</label>
                <input
                  type="text"
                  value={form.nombre}
                  onChange={e => setForm({ ...form, nombre: e.target.value })}
                  placeholder="Ej: Viaje a la playa"
                  required
                />
              </div>
              <div className="form-group">
                <label>Monto objetivo</label>
                <input
                  type="number"
                  value={form.monto_objetivo}
                  onChange={e => setForm({ ...form, monto_objetivo: e.target.value })}
                  placeholder="¿Cuánto necesitas?"
                  required
                  min="1"
                />
              </div>
              <div className="form-group">
                <label>Fecha límite (opcional)</label>
                <input
                  type="date"
                  value={form.fecha_objetivo}
                  onChange={e => setForm({ ...form, fecha_objetivo: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Descripción (opcional)</label>
                <input
                  type="text"
                  value={form.descripcion}
                  onChange={e => setForm({ ...form, descripcion: e.target.value })}
                  placeholder="Para qué es esta meta"
                />
              </div>
            </div>
            <div className="form-group" style={{ marginTop: 8 }}>
              <label>Icono</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 6 }}>
                {ICONOS.map(ic => (
                  <button
                    key={ic}
                    type="button"
                    onClick={() => setForm({ ...form, icono: ic })}
                    style={{
                      width: 38,
                      height: 38,
                      fontSize: '1.3rem',
                      borderRadius: 8,
                      border: form.icono === ic ? '2px solid var(--primary)' : '2px solid var(--border)',
                      background: form.icono === ic ? 'var(--primary-xlight)' : 'white',
                      cursor: 'pointer',
                    }}
                  >
                    {ic}
                  </button>
                ))}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button type="submit" className="btn-primary" disabled={saving}>
                {saving ? 'Creando...' : 'Crear meta'}
              </button>
              <button type="button" className="btn-secondary" onClick={() => setShowForm(false)}>
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Metas activas */}
      {metasActivas.length === 0 && metasCompletadas.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-muted)', background: 'white', borderRadius: 12 }}>
          <div style={{ fontSize: '3rem', marginBottom: 16 }}>🎯</div>
          <p style={{ fontWeight: 600, marginBottom: 8 }}>Aún no tienes metas</p>
          <p style={{ fontSize: '0.9rem' }}>Crea tu primera meta y empieza a ahorrar con propósito.</p>
        </div>
      ) : (
        <>
          {metasActivas.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <h3 style={{ marginBottom: 16, color: 'var(--text-secondary)', fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: 1 }}>
                En progreso
              </h3>
              <div style={{ display: 'grid', gap: 16, gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
                {metasActivas.map(meta => {
                  const actual = ahorrado[meta.id] || 0;
                  const pct = meta.monto_objetivo > 0
                    ? Math.min(100, Math.round((actual / meta.monto_objetivo) * 100))
                    : 0;
                  const falta = Math.max(0, meta.monto_objetivo - actual);

                  if (editingId === meta.id) {
                    return (
                      <div key={meta.id} className="card">
                        <form onSubmit={handleSaveEdit}>
                          <div className="form-group">
                            <label>Nombre</label>
                            <input type="text" value={editForm.nombre} onChange={e => setEditForm({ ...editForm, nombre: e.target.value })} required />
                          </div>
                          <div className="form-group">
                            <label>Objetivo</label>
                            <input type="number" value={editForm.monto_objetivo} onChange={e => setEditForm({ ...editForm, monto_objetivo: e.target.value })} required />
                          </div>
                          <div className="form-group">
                            <label>Fecha límite</label>
                            <input type="date" value={editForm.fecha_objetivo} onChange={e => setEditForm({ ...editForm, fecha_objetivo: e.target.value })} />
                          </div>
                          <div className="form-group">
                            <label>Descripción</label>
                            <input type="text" value={editForm.descripcion} onChange={e => setEditForm({ ...editForm, descripcion: e.target.value })} />
                          </div>
                          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                            <button type="submit" className="btn-primary" disabled={saving}>Guardar</button>
                            <button type="button" className="btn-secondary" onClick={() => setEditingId(null)}>Cancelar</button>
                          </div>
                        </form>
                      </div>
                    );
                  }

                  return (
                    <div key={meta.id} className="card meta-card">
                      {/* Encabezado: icono + nombre + porcentaje */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                        <span style={{ fontSize: '2rem', lineHeight: 1, flexShrink: 0 }}>{meta.icono || '🎯'}</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p style={{ fontWeight: 700, margin: 0, color: 'var(--text)', fontSize: '1rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{meta.nombre}</p>
                          {meta.descripcion && (
                            <p style={{ margin: 0, fontSize: '0.78rem', color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{meta.descripcion}</p>
                          )}
                        </div>
                        <span style={{ fontWeight: 800, fontSize: '1.3rem', color: 'var(--primary)', flexShrink: 0 }}>{pct}%</span>
                      </div>

                      {/* Barra de progreso */}
                      <ProgressBar value={actual} max={meta.monto_objetivo} />

                      {/* Montos */}
                      <div style={{ marginTop: 6, marginBottom: 4 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: 2 }}>
                          <span>Ahorrado: <strong className="text-success">{formatCLP(actual)}</strong></span>
                          <span>Objetivo: <strong>{formatCLP(meta.monto_objetivo)}</strong></span>
                        </div>
                        <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', margin: '2px 0 0' }}>
                          Te faltan <strong style={{ color: 'var(--primary)' }}>{formatCLP(falta)}</strong>
                          {meta.fecha_objetivo && (
                            <span style={{ marginLeft: 8 }}>· Límite: {formatFecha(meta.fecha_objetivo)}</span>
                          )}
                        </p>
                        <DetalleReservas meta={meta} />
                      </div>

                      {/* Botones */}
                      <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
                        <button
                          className="btn-primary"
                          style={{ flex: 1, minWidth: 80, padding: '8px 10px', fontSize: '0.85rem' }}
                          onClick={() => abrirAbono(meta)}
                          disabled={bolsillos.length === 0}
                          title={bolsillos.length === 0 ? 'Primero crea una categoría' : ''}
                        >
                          + Abonar
                        </button>
                        {actual > 0 && (
                          <button className="btn-secondary" style={{ padding: '8px 10px', fontSize: '0.85rem' }}
                            onClick={() => abrirRetiro(meta)} disabled={saving}>
                            Retirar
                          </button>
                        )}
                        <button className="btn-secondary" style={{ padding: '8px 10px', fontSize: '0.85rem' }} onClick={() => handleEdit(meta)}>✎</button>
                        <button className="btn-secondary" style={{ padding: '8px 10px', fontSize: '0.85rem', color: 'var(--danger)' }}
                          onClick={() => handleDelete(meta.id)}>×</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {metasCompletadas.length > 0 && (
            <div>
              <h3 style={{ marginBottom: 16, color: 'var(--text-secondary)', fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: 1 }}>
                Completadas 🏆
              </h3>
              <div style={{ display: 'grid', gap: 16, gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
                {metasCompletadas.map(meta => {
                  const actual = ahorrado[meta.id] || 0;
                  return (
                    <div key={meta.id} className="card" style={{ opacity: 0.85, border: '2px solid var(--success, #22c55e)' }}>
                      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 8 }}>
                        <span style={{ fontSize: '1.8rem' }}>{meta.icono || '🎯'}</span>
                        <div>
                          <p style={{ fontWeight: 700, margin: 0 }}>{meta.nombre}</p>
                          <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--success, #16a34a)', fontWeight: 600 }}>
                            ¡Meta alcanzada! {formatCLP(actual)}
                          </p>
                          <DetalleReservas meta={meta} />
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        {actual > 0 && (
                          <button
                            className="btn-secondary"
                            style={{ padding: '6px 12px', fontSize: '0.82rem' }}
                            onClick={() => abrirRetiro(meta)}
                            disabled={saving}
                          >
                            Retirar fondos
                          </button>
                        )}
                        <button
                          className="btn-secondary"
                          style={{ padding: '6px 12px', fontSize: '0.82rem', color: 'var(--danger)' }}
                          onClick={() => handleDelete(meta.id)}
                        >
                          Eliminar
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}

      {/* Modal de abono / retiro */}
      {movModal && (() => {
        const { tipo, meta } = movModal;
        const esAbono = tipo === 'abono';
        const reservas = Object.entries(reservadoMetaPorBolsillo(abonosMeta, meta.id)).filter(([, v]) => v > 0);
        const maximo = esAbono
          ? (movForm.bolsillo_id ? fin.porBolsillo(movForm.bolsillo_id).disponible : 0)
          : (reservadoMetaPorBolsillo(abonosMeta, meta.id)[movForm.bolsillo_id] || 0);
        return (
          <div className="modal-overlay" onClick={() => setMovModal(null)}>
            <div className="modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3>{meta.icono} {esAbono ? 'Abonar a' : 'Retirar de'} "{meta.nombre}"</h3>
                <button className="modal-close" onClick={() => setMovModal(null)}>×</button>
              </div>
              <p style={{ margin: '0 0 16px', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                {esAbono
                  ? 'El dinero sigue en la categoría, pero queda reservado para esta meta: baja tu disponible, no tu saldo.'
                  : 'El dinero vuelve a quedar disponible en la categoría. Si además lo gastaste, registra la salida de dinero en esa categoría.'}
              </p>
              <form onSubmit={handleMovMeta}>
                <div className="form-group">
                  <label>{esAbono ? '¿De qué categoría sale el ahorro?' : '¿De dónde liberas el dinero?'}</label>
                  <select value={movForm.bolsillo_id} onChange={e => setMovForm({ ...movForm, bolsillo_id: e.target.value })}>
                    {esAbono
                      ? bolsillos.map(b => (
                          <option key={b.id} value={b.id}>{b.nombre} — disponible {formatCLP(fin.porBolsillo(b.id).disponible)}</option>
                        ))
                      : reservas.map(([id, v]) => (
                          <option key={id || 'sin'} value={id}>{nombreBolsillo(id)} — reservado {formatCLP(v)}</option>
                        ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Monto ($)</label>
                  <input
                    type="number"
                    value={movForm.monto}
                    onChange={e => setMovForm({ ...movForm, monto: e.target.value })}
                    placeholder="0"
                    required
                    min="1"
                    max={Math.max(0, maximo)}
                    autoFocus
                  />
                  <span className="field-hint">Máximo: {formatCLP(Math.max(0, maximo))}</span>
                </div>
                <div className="modal-actions">
                  <button type="button" className="btn-secondary" onClick={() => setMovModal(null)}>Cancelar</button>
                  <button type="submit" className="btn-primary" disabled={saving}>
                    {saving ? 'Guardando...' : esAbono ? 'Abonar' : 'Retirar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

export default Metas;
